import { compoundV3OraclesData, compoundV3BaseData } from '@1delta/data-sdk'
import {
  CompoundV3QueryMeta,
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { formatUnits } from 'viem'
import { createMarketUid } from '../../../utils'
import { Lender } from '@1delta/lender-registry'

/**
 * Chainlink Aggregator ABI - latestRoundData
 */
export const ChainlinkAggregatorAbi = [
  {
    inputs: [],
    name: 'latestRoundData',
    outputs: [
      { internalType: 'uint80', name: 'roundId', type: 'uint80' },
      { internalType: 'int256', name: 'answer', type: 'int256' },
      { internalType: 'uint256', name: 'startedAt', type: 'uint256' },
      { internalType: 'uint256', name: 'updatedAt', type: 'uint256' },
      { internalType: 'uint80', name: 'answeredInRound', type: 'uint80' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
] as const

/** Index of 'answer' in latestRoundData response */
const ANSWER_INDEX = 1

/** Index of 'updatedAt' in latestRoundData response */
const UPDATED_AT_INDEX = 3

/** Chainlink price feeds use 8 decimals */
const CHAINLINK_DECIMALS = 8

/**
 * Override map for lender/chain combos where oracle prices are already in USD
 * and should NOT be scaled by the base asset price.
 * Key: lender name, Value: Set of chainIds where this applies
 */
const USD_PRICED_OVERRIDES: Record<string, Set<string>> = {
  [Lender.COMPOUND_V3_AERO]: new Set(['8453']), // BASE - prices already in USD
  [Lender.COMPOUND_V3_USDBC]: new Set(['8453']), // BASE - prices already in USD
  // Ronin WRON comet quotes in USD, NOT in its WRON base. Verified on-chain
  // 2026-09-16 (comet 0xc0Afdb…c0c0): USDC feed reads 1.00000071, WETH
  // 2415.66, WRON 0.0479 — all USD. Without this override every feed gets
  // multiplied by WRON/USD (~0.05), which shipped WRON at 9.4e-7 USD. Do NOT
  // generalize from the sibling WETH comet (0x4006eD…6DFE): that one IS
  // base-quoted (constant feed on WETH, RON/ETH = 2e-5) and needs the scaling.
  [Lender.COMPOUND_V3_WRON]: new Set(['2020']),
}

/**
 * Get multicall calls for Compound V3 oracles
 * Uses per-asset oracle map with latestRoundData()
 * Includes base asset info from compoundV3BaseData for proper price scaling
 */
export function getCompoundV3Calls(
  chainId: string,
): FetcherResult<CompoundV3QueryMeta[]>[] {
  const oraclesData = compoundV3OraclesData() ?? {}
  const baseData = compoundV3BaseData() ?? {}
  const results: FetcherResult<CompoundV3QueryMeta[]>[] = []

  for (const [lender, chainOracles] of Object.entries(oraclesData)) {
    const assetOracles = chainOracles[chainId]
    if (!assetOracles) continue

    // Skip base asset for lender/chain combos where prices are already in USD
    const skipBaseScaling = USD_PRICED_OVERRIDES[lender]?.has(chainId)
    const baseAsset = skipBaseScaling
      ? undefined
      : baseData[lender]?.[chainId]?.baseAsset?.toLowerCase()

    // Detect whether this lender/chain has a constant price feed for its
    // base asset. If so, mark only that entry with staticBase=true.
    const entries = Object.entries(assetOracles)
    const hasConstantFeed = entries.some(([, e]) =>
      e.description?.toLowerCase().includes('constant price'),
    )

    const calls: MulticallCall[] = []
    const meta: CompoundV3QueryMeta[] = []

    for (const [asset, entry] of entries) {
      calls.push({
        address: entry.oracle,
        name: 'latestRoundData',
        params: [],
      })

      // Only flag staticBase on the asset that IS the base asset
      const isBaseAsset =
        hasConstantFeed && baseAsset && asset.toLowerCase() === baseAsset

      meta.push({
        asset,
        oracle: entry.oracle,
        baseAsset,
        lender,
        description: entry.description,
        staticBase: isBaseAsset || undefined,
      })
    }

    if (calls.length > 0) {
      results.push({
        calls,
        meta,
        lender,
      })
    }
  }

  return results
}

/**
 * Parse Compound V3 oracle results into structured price entries
 * Uses latestRoundData() - answer is at index 1, 8 decimals
 *
 * Compound V3 prices are denominated in the base asset (e.g., USDC, WETH).
 * If we have the base asset's USD price, we can calculate USD prices.
 * Otherwise, we return the price as-is (denominated in base asset).
 */
export function parseCompoundV3Results(
  data: any[],
  meta: CompoundV3QueryMeta[],
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  data.forEach((rawResult, index) => {
    const queryMeta = meta[index]
    if (!queryMeta || rawResult === undefined || rawResult === '0x') return

    try {
      // latestRoundData returns a tuple, answer is at index 1
      const answer = Array.isArray(rawResult)
        ? rawResult[ANSWER_INDEX]
        : rawResult
      if (!answer) return

      const rawPrice = Number(
        formatUnits(BigInt(answer.toString()), CHAINLINK_DECIMALS),
      )
      if (isNaN(rawPrice) || rawPrice === 0) return

      const assetLc = queryMeta.asset.toLowerCase()
      let priceUSD = rawPrice

      // If we have a base asset, scale the price to USD
      if (queryMeta.baseAsset) {
        const baseAssetLc = queryMeta.baseAsset.toLowerCase()
        const baseOracleKey =
          tokenList?.[baseAssetLc]?.assetGroup ?? `${chainId}-${baseAssetLc}`
        const baseAssetUSD = usdPrices[baseOracleKey] ?? usdPrices[baseAssetLc]

        // No base USD price → we cannot produce a USD figure this cycle.
        // Drop the entry rather than publish the base-denominated raw as
        // `priceUSD`: that value feeds selectAssetGroupPrices → asset-group
        // USD prices, and on a freshly added chain (token list still sparse)
        // it ships e.g. RON/ETH = 2e-5 as "2e-5 USD", which then poisons the
        // next cycle's usdPrices input. Absent beats wrong by 4 orders.
        if (!baseAssetUSD) return

        // Price is in base asset terms, convert to USD
        priceUSD = rawPrice * baseAssetUSD
      }

      // Chainlink round timestamp — consumed by the staleness diagnostics
      // and by the staleness demotion in `selectAssetGroupPrices`.
      const updatedAtRaw = Array.isArray(rawResult)
        ? Number(rawResult[UPDATED_AT_INDEX])
        : 0

      entries.push({
        asset: assetLc,
        price: rawPrice, // Raw price (in base asset terms if applicable)
        priceUSD,
        marketUid: createMarketUid(chainId, queryMeta.lender, queryMeta.asset),
        description: queryMeta.description,
        staticBase: queryMeta.staticBase || undefined,
        baseAsset: queryMeta.baseAsset,
        updatedAt: updatedAtRaw > 0 ? updatedAtRaw : undefined,
        feedSource: updatedAtRaw > 0 ? queryMeta.oracle : undefined,
      })
    } catch {
      // Skip invalid prices
    }
  })

  return entries
}

/**
 * Get ABI for Compound V3 oracle calls
 */
export function getCompoundV3Abi() {
  return ChainlinkAggregatorAbi
}

/**
 * Compound V3 Oracle Fetcher module
 */
export const compoundV3Fetcher = {
  getCalls: getCompoundV3Calls,
  parse: parseCompoundV3Results,
  getAbi: getCompoundV3Abi,
}
